import { PaymentProcessorEnum } from '@systemeio/ui'
import { CONNECTED_PAYMENT_INTEGRATIONS_API } from 'modules/profile/payment-gateways/api/payment-gateways-api'
import {
  PaymentIntegrationsArrayType,
  PaymentIntegrationsType,
} from 'modules/profile/payment-gateways/types/connected-payment-integrations-interface'
import { useCallback } from 'react'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import useSWR from 'swr'

interface ProcessorField {
  processor: PaymentProcessorEnum
  matcher?: never
}

interface MatcherField<T extends PaymentIntegrationsType> {
  matcher: (paymentProcessor: T['paymentProcessor']) => boolean
  processor?: never
}

type PaymentProcessorConfig<T extends PaymentIntegrationsType> = ProcessorField | MatcherField<T>

export function useConnectedPaymentIntegrations() {
  const { user } = useUser()

  const { fetcher } = useFetch<RequestMethodsEnum.get, PaymentIntegrationsArrayType>({
    method: RequestMethodsEnum.get,
  })

  const { data: paymentIntegrations, mutate: mutatePaymentIntegrations } = useSWR(() => {
    if (user) {
      return CONNECTED_PAYMENT_INTEGRATIONS_API
    }
    return null
  }, fetcher)

  const getConnectedPaymentIntegration = useCallback(
    <T extends PaymentIntegrationsType = PaymentIntegrationsType>({
      matcher,
      processor,
    }: PaymentProcessorConfig<T>): T | undefined =>
      paymentIntegrations?.find((integration): integration is T =>
        matcher
          ? matcher(integration.paymentProcessor)
          : integration.paymentProcessor === processor,
      ),
    [paymentIntegrations],
  )
  const removePaymentIntegration = useCallback(
    <T extends PaymentIntegrationsType = PaymentIntegrationsType>({
      matcher,
      processor,
    }: PaymentProcessorConfig<T>) =>
      mutatePaymentIntegrations(
        data =>
          data &&
          data.filter(integration =>
            matcher
              ? matcher(integration.paymentProcessor)
              : integration.paymentProcessor !== processor,
          ),
        false,
      ),
    [mutatePaymentIntegrations],
  )
  return {
    paymentIntegrations,
    mutatePaymentIntegrations,
    removePaymentIntegration,
    getConnectedPaymentIntegration,
  }
}
